import React, { useState, useEffect } from 'react';
import Header from "./common/Header";
import { getLlmSettings, getOpenAiModels, saveLlmSettings } from '../api';

const PROVIDERS = [['azure', 'Azure OpenAI'], ['openai', 'OpenAI']];
const THINKING_LEVELS = ['none', 'low', 'medium', 'high'];

// Loads the saved choice, and OpenAI's live model list once OpenAI is picked.
const useLlmSettings = (provider) => {
    const [settings, setSettings] = useState(null);
    const [openAiModels, setOpenAiModels] = useState([]);
    const [error, setError] = useState(null);
    useEffect(() => {
        getLlmSettings().then(setSettings).catch((err) => setError(err.message));
    }, []);
    useEffect(() => {
        if (provider !== 'openai' || openAiModels.length > 0) return;
        getOpenAiModels().then(setOpenAiModels).catch((err) => setError(err.message));
    }, [provider, openAiModels.length]);
    return { settings, setSettings, openAiModels, error };
};

const modelChoices = (openAiModels, current) =>
    current && !openAiModels.includes(current) ? [current, ...openAiModels] : openAiModels;

// One connection's OpenAI choice: model, temperature and thinking. Blank = not sent.
const OpenAiChoice = ({ choice, models, disabled, onChange }) => (
    <>
        <td>
            <select value={choice.model || ''} disabled={disabled}
                    onChange={(e) => onChange({ ...choice, model: e.target.value })}>
                {modelChoices(models, choice.model).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
        </td>
        <td>
            <input type="number" min="0" max="2" step="0.1" style={{ width: 70 }} disabled={disabled}
                   value={choice.temperature ?? ''} placeholder="not sent"
                   onChange={(e) => onChange({ ...choice,
                       temperature: e.target.value === '' ? null : Number(e.target.value) })} />
        </td>
        <td>
            <select value={choice.reasoning_effort || ''} disabled={disabled}
                    onChange={(e) => onChange({ ...choice, reasoning_effort: e.target.value || null })}>
                <option value="">not sent</option>
                {THINKING_LEVELS.map((level) => <option key={level} value={level}>{level}</option>)}
            </select>
        </td>
    </>
);

// Admin page: choose Azure OpenAI or OpenAI for the whole app, and OpenAI's model and settings
// per connection. The server tries each OpenAI choice with a tiny call before saving it.
const LlmSettings = () => {
    const [provider, setProvider] = useState(null);
    const [choices, setChoices] = useState({});
    const [message, setMessage] = useState(null);
    const { settings, setSettings, openAiModels, error } = useLlmSettings(provider);

    useEffect(() => {
        if (!settings) return;
        setProvider(settings.provider);
        setChoices(Object.fromEntries(settings.connections.map((c) => [c.name, c.openai])));
    }, [settings]);

    const handleSave = async () => {
        setMessage(provider === 'openai' ? 'Checking each model with OpenAI...' : null);
        try {
            setSettings(await saveLlmSettings(provider, provider === 'openai' ? choices : {}));
            setMessage('Saved. It applies from the next LLM call, everywhere in the app.');
        } catch (err) {
            setMessage(`Not saved: ${err.message}`);
        }
    };

    return (
        <div>
            <Header />
            <main>
                <div className="pof2frms-container">
                    <div className="pof2frms-head">
                        <h1>Settings</h1>
                    </div>
                    {error && <p>{error}</p>}
                    {!settings && !error && <p>Loading...</p>}
                    {settings && (
                        <>
                            <p style={{ border: '1px solid #d93025', background: '#fdecea', color: '#a50e0e',
                                        padding: '10px 14px', borderRadius: 4 }}>
                                <strong>Warning:</strong> changing the provider or models can change or remove
                                results. Orders may come out differently, and some lines or products may be
                                missed. Run the self evaluation after any change.
                            </p>
                            <h3>LLM provider</h3>
                            {PROVIDERS.map(([value, label]) => (
                                <label key={value} style={{ marginRight: 24 }}>
                                    <input type="radio" name="provider" value={value}
                                           checked={provider === value}
                                           disabled={value === 'openai' && !settings.openai_available}
                                           onChange={() => setProvider(value)} /> {label}
                                </label>
                            ))}
                            {!settings.openai_available && <p>OpenAI keys are not set up on this server.</p>}
                            <div className="po2frms-table" style={{ marginTop: 16 }}>
                                <table>
                                    <thead>
                                        <tr><th>Connection</th><th>Azure model (fixed)</th><th>OpenAI model</th>
                                            <th>Temperature</th><th>Thinking</th></tr>
                                    </thead>
                                    <tbody>
                                        {settings.connections.map((c) => (
                                            <tr key={c.name}>
                                                <td>{c.name}</td>
                                                <td>{c.azure_model}</td>
                                                <OpenAiChoice choice={choices[c.name] || {}} models={openAiModels}
                                                              disabled={provider !== 'openai'}
                                                              onChange={(choice) => setChoices({ ...choices, [c.name]: choice })} />
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <p>gpt-5.6 accepts a temperature other than 1 only with thinking "none".
                                Batches already running finish partly on the old choice. Selection sheet embeddings stay on Azure.</p>
                            <button className="btn" onClick={handleSave}>Save</button>
                            {message && <p>{message}</p>}
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

export default LlmSettings;
